/**
 * Main theme configuration for Material-UI v7
 * Climadiag Commune application theme
 * Migrated and modernized from legacy Gatsby theme
 */
import { createTheme } from '@mui/material/styles';
import { palette } from './palette';
import { typography } from './typography';
import { breakpoints } from './breakpoints';

/**
 * Component overrides for consistent styling
 */
const components = {
  // Button customizations
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        textTransform: 'none',
        fontWeight: 700,
        padding: '8px 24px',
        // Keyboard-only focus ring (WCAG 2.4.7). MUI resets the outline, so it
        // has to be restored. Keep the browser's own "auto" outline rather than
        // a hard-coded colour: it adapts to the user's system theme and
        // high-contrast settings, and follows the button's border-radius.
        '&:focus-visible': {
          outline: 'auto',
          outlineOffset: '2px',
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
  },

  // Card customizations
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
    },
  },

  // Paper customizations
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
      elevation1: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
    },
  },

  // Container customizations
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: 16,
        paddingRight: 16,
        '@media (min-width: 600px)': {
          paddingLeft: 24,
          paddingRight: 24,
        },
      },
    },
  },

  // Typography customizations
  MuiTypography: {
    styleOverrides: {
      gutterBottom: {
        marginBottom: '0.75em',
      },
    },
  },

  // Chip customizations for climate indicators
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontWeight: 600,
      },
    },
  },

  // Dialog customizations
  MuiDialog: {
    defaultProps: {
      PaperProps: {
        elevation: 8,
      },
    },
  },
};

/**
 * Spacing configuration
 */
const spacing = 8; // Base unit: 8px

/**
 * Create the base theme
 */
const baseTheme = createTheme({
  palette,
  typography,
  breakpoints,
  spacing,
  components,

  // Shape configuration
  shape: {
    borderRadius: 8,
  },

  // Shadow configuration
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 2px 4px rgba(0, 0, 0, 0.1)',
    '0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.15)',
    '0px 16px 32px rgba(0, 0, 0, 0.2)',
    '0px 20px 40px rgba(0, 0, 0, 0.25)',
    '0px 24px 48px rgba(0, 0, 0, 0.3)',
    // Add remaining shadows for complete array (24 total required)
    ...Array.from({ length: 15 }, () => '0px 24px 48px rgba(0, 0, 0, 0.3)')
  ],

  // Transitions
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },

  // Z-index values
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
});

/**
 * Create responsive theme with font scaling
 */
const theme = createTheme(baseTheme, {
  // Add responsive typography
  typography: {
    ...baseTheme.typography,
    h1: {
      ...baseTheme.typography.h1,
      fontSize: 'clamp(2.5rem, 5vw, 4.3rem)',
    },
    h2: {
      ...baseTheme.typography.h2,
      fontSize: 'clamp(2rem, 4vw, 3.5rem)',
    },
    h3: {
      ...baseTheme.typography.h3,
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
    },
  },
});

export default theme;
